import { ArrowDown, ArrowUp, GalleryHorizontal, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { AssetResponse, CopyElement } from '../../api';
import { FRAME_COPY_OPTIONS } from '../../constants/taxonomy';
import { SourceImageSelect } from '../grid/SourceImagePicker';
import { CopyElementsEditor } from '../metadata/CopyElementsEditor';
import {
  framesWithoutSource,
  isCarouselUploadable,
  moveFrame,
  removeFrame,
  removeRejected,
  updateCarousel,
  type CarouselFrameDraft,
  type DetectedCarousel,
} from './carouselDetect';

/**
 * The carousel cards in the upload modal: arranging frames at the select
 * step, and each frame's source image and copy at the details step.
 *
 * Separate from the modal because a carousel is the one kind whose items have
 * parts to edit before upload; the modal only holds the list and passes edits
 * back through `onChange`.
 */

/** A local preview of a frame, released when the row goes away. */
function FrameThumb({ file, className }: { file: File; className: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url ? (
    <img src={url} alt="" className={`${className} rounded object-cover`} />
  ) : (
    <div className={`${className} rounded bg-slate-100`} />
  );
}

function frameSizeLabel(frame: CarouselFrameDraft): string {
  return frame.size ? `${frame.size.width}×${frame.size.height}` : 'size unknown';
}

interface CarouselListProps {
  carousels: DetectedCarousel[];
  onChange: (carousels: DetectedCarousel[]) => void;
}

/** Select step: name each carousel, put its frames in order, drop what does not belong. */
export function CarouselSelectList({ carousels, onChange }: CarouselListProps) {
  const replace = (index: number, next: DetectedCarousel) =>
    onChange(carousels.map((carousel, i) => (i === index ? next : carousel)));
  const remove = (index: number) => onChange(carousels.filter((_, i) => i !== index));

  return (
    <ul className="space-y-2">
      {carousels.map((carousel, index) => {
        const blocked = !isCarouselUploadable(carousel);
        return (
          <li
            key={carousel.folder || `loose-${index}`}
            className={`rounded-md border p-2.5 ${
              blocked ? 'border-red-200 bg-red-50/40' : 'border-slate-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <GalleryHorizontal
                className={`h-3.5 w-3.5 shrink-0 ${blocked ? 'text-red-500' : 'text-slate-400'}`}
                strokeWidth={2}
              />
              <input
                type="text"
                value={carousel.name}
                maxLength={200}
                onChange={(event) =>
                  replace(index, updateCarousel(carousel, { name: event.target.value }))
                }
                aria-label="Carousel name"
                className="min-w-0 flex-1 rounded border border-transparent px-1 py-0.5 text-[12.5px] font-medium text-slate-800 outline-none hover:border-slate-200 focus:border-[#0014cc]"
              />
              <span className="shrink-0 text-[11px] text-slate-400">
                {carousel.frames.length} frame{carousel.frames.length === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove ${carousel.name || 'carousel'}`}
                className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </div>

            <ol className="mt-2 space-y-1">
              {carousel.frames.map((frame, position) => (
                <li key={frame.key} className="flex items-center gap-2">
                  <span className="w-4 shrink-0 text-right text-[11px] text-slate-400">
                    {position + 1}
                  </span>
                  <FrameThumb file={frame.file} className="h-9 w-9 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-[12px] text-slate-700">
                    {frame.file.name}
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">
                    {frameSizeLabel(frame)}
                  </span>
                  <button
                    type="button"
                    disabled={position === 0}
                    onClick={() => replace(index, moveFrame(carousel, position, position - 1))}
                    aria-label={`Move ${frame.file.name} up`}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
                  >
                    <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    disabled={position === carousel.frames.length - 1}
                    onClick={() => replace(index, moveFrame(carousel, position, position + 1))}
                    aria-label={`Move ${frame.file.name} down`}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
                  >
                    <ArrowDown className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => replace(index, removeFrame(carousel, frame.key))}
                    aria-label={`Remove ${frame.file.name}`}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </li>
              ))}
              {carousel.rejected.map((filename) => (
                <li key={`rejected-${filename}`} className="flex items-center gap-2">
                  <span className="w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-[12px] text-red-700 line-through">
                    {filename}
                  </span>
                  <button
                    type="button"
                    onClick={() => replace(index, removeRejected(carousel, filename))}
                    aria-label={`Remove ${filename}`}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </li>
              ))}
            </ol>

            {/* Listed, never dropped: a folder of four turning into three
                carousels with no word about the fourth is the worst version. */}
            {carousel.issues.map((issue) => (
              <p key={issue} className="mt-1 text-[11px] text-red-700">
                {issue}
              </p>
            ))}
          </li>
        );
      })}
    </ul>
  );
}

interface CarouselFrameDetailsProps extends CarouselListProps {
  sourceImageOptions: AssetResponse[];
}

/** Details step: every frame's source image (required) and copy. */
export function CarouselFrameDetails({
  carousels,
  onChange,
  sourceImageOptions,
}: CarouselFrameDetailsProps) {
  const setFrame = (
    carouselIndex: number,
    frameKey: string,
    patch: Partial<Pick<CarouselFrameDraft, 'sourceImageId' | 'copy'>>,
  ) =>
    onChange(
      carousels.map((carousel, i) =>
        i === carouselIndex
          ? updateCarousel(carousel, {
              frames: carousel.frames.map((frame) =>
                frame.key === frameKey ? { ...frame, ...patch } : frame,
              ),
            })
          : carousel,
      ),
    );

  return (
    <div className="space-y-4">
      {carousels.map((carousel, carouselIndex) => {
        const unsourced = framesWithoutSource(carousel);
        return (
          <section
            key={carousel.folder || `loose-${carouselIndex}`}
            className="rounded-md border border-slate-200"
          >
            <header className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
              <GalleryHorizontal className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
              <h3 className="truncate text-[12.5px] font-semibold text-slate-800">
                {carousel.name}
              </h3>
              <span
                className={`ml-auto shrink-0 text-[11px] ${
                  unsourced.length ? 'text-amber-700' : 'text-slate-400'
                }`}
              >
                {unsourced.length
                  ? `Source image missing on frame ${unsourced.join(', ')}`
                  : 'Every frame has a source image'}
              </span>
            </header>
            <ol className="divide-y divide-slate-100">
              {carousel.frames.map((frame, position) => (
                <li key={frame.key} className="grid grid-cols-[96px_1fr] gap-3 px-3 py-3">
                  <div className="flex flex-col gap-1">
                    <FrameThumb file={frame.file} className="h-24 w-24" />
                    <span className="text-[11px] font-medium text-slate-600">
                      Frame {position + 1}
                    </span>
                    <span className="truncate text-[10.5px] text-slate-400" title={frame.file.name}>
                      {frame.file.name}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-col gap-3">
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        Source image <span className="text-amber-500">*</span>
                      </span>
                      <SourceImageSelect
                        single
                        required
                        value={frame.sourceImageId ? [frame.sourceImageId] : []}
                        onChange={(ids) =>
                          setFrame(carouselIndex, frame.key, { sourceImageId: ids[0] ?? '' })
                        }
                        options={sourceImageOptions}
                        emptyLabel="Pick the source image this frame was cut from"
                        aria-label={`Source image for frame ${position + 1}`}
                      />
                    </label>
                    <div className="flex flex-col gap-1">
                      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        Copy
                      </span>
                      <CopyElementsEditor
                        value={frame.copy}
                        onChange={(copy: CopyElement[]) =>
                          setFrame(carouselIndex, frame.key, { copy })
                        }
                        typeOptions={FRAME_COPY_OPTIONS}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}
